import React, { useState, useEffect, useCallback, useRef } from 'react';

const API_URL = process.env.REACT_APP_API_URL || '';

/**
 * Parse usageCost string to €/kWh numeric value.
 * Handles formats like "0,47€/kWh", "0.47 EUR/kWh", "€0.39/kWh", etc.
 */
function parsePrice(usageCost) {
  if (!usageCost) return null;
  const s = usageCost.replace(',', '.').toLowerCase();
  // Try to extract number near €/kWh or eur/kwh
  const match = s.match(/([\d.]+)\s*[€eur]*\s*\/?\s*kwh/);
  if (match) return parseFloat(match[1]);
  // Try just a number with € sign
  const match2 = s.match(/[€]\s*([\d.]+)/);
  if (match2) return parseFloat(match2[1]);
  const match3 = s.match(/([\d.]+)\s*[€]/);
  if (match3) return parseFloat(match3[1]);
  return null;
}

/**
 * Estimate charge time in minutes given kWh to charge and charger power (kW).
 * Uses a simplified charging curve: full speed up to 80%, then slower.
 */
function estimateChargeTime(kwhToCharge, chargerPowerKw, carMaxPower, batteryCapacity, startPct, endPct) {
  const effectivePower = Math.min(chargerPowerKw, carMaxPower);
  // Simplified: below 80% charge at full power, above 80% at 50% power
  const threshold = 0.8 * batteryCapacity;
  const startKwh = (startPct / 100) * batteryCapacity;
  const endKwh = (endPct / 100) * batteryCapacity;
  
  let time = 0; // hours
  if (endKwh <= threshold) {
    time = (endKwh - startKwh) / effectivePower;
  } else if (startKwh >= threshold) {
    time = (endKwh - startKwh) / (effectivePower * 0.5);
  } else {
    time = (threshold - startKwh) / effectivePower + (endKwh - threshold) / (effectivePower * 0.5);
  }
  return Math.max(0, time * 60); // minutes
}

/**
 * Calculate haversine distance in km between two points.
 */
function haversineKm(lat1, lng1, lat2, lng2) {
  const R = 6371;
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLng = (lng2 - lng1) * Math.PI / 180;
  const a = Math.sin(dLat / 2) ** 2 + Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

/**
 * Find point on route polyline closest to a given lat/lng.
 * Returns { index, distance, point }.
 */
function findClosestRoutePoint(routeCoords, lat, lng) {
  let minDist = Infinity;
  let bestIdx = 0;
  for (let i = 0; i < routeCoords.length; i++) {
    const [rLng, rLat] = routeCoords[i];
    const d = haversineKm(lat, lng, rLat, rLng);
    if (d < minDist) { minDist = d; bestIdx = i; }
  }
  return { index: bestIdx, distance: minDist };
}

/**
 * Calculate distance along route from start to a given point index.
 */
function distanceAlongRoute(routeCoords, toIndex, totalDistanceKm) {
  if (!routeCoords || routeCoords.length < 2) return 0;
  // Approximate: ratio of index to total coords * totalDistance
  return (toIndex / (routeCoords.length - 1)) * totalDistanceKm;
}

/**
 * Main route calculation function.
 * Given the route geometry, car config, and available chargers, compute optimal stops.
 */
function computeRouteOptions(routeCoords, totalDistanceKm, evCarConfig, chargers, favoriteChargers, maxDeviation, priorityFavorites = false) {
  if (!routeCoords || !totalDistanceKm || !evCarConfig || !chargers || chargers.length === 0) return null;

  const { consumption, batteryCapacity, maxChargePower, startBattery, arrivalBattery, minBatteryStop, maxBatteryCharge } = evCarConfig;
  if (!consumption || !batteryCapacity) return null;

  // km per kWh
  const kmPerKwh = 100 / consumption;
  
  // Available range from start
  const startRangeKm = ((startBattery - minBatteryStop) / 100) * batteryCapacity * kmPerKwh;
  // Available range after charge
  const chargeRangeKm = ((maxBatteryCharge - minBatteryStop) / 100) * batteryCapacity * kmPerKwh;
  // Can we make it without stops?
  const directRangeKm = ((startBattery - arrivalBattery) / 100) * batteryCapacity * kmPerKwh;
  if (directRangeKm >= totalDistanceKm) return { noStopsNeeded: true };

  // Map chargers to route positions
  const chargersOnRoute = chargers
    .filter(c => c.coordinates && c.coordinates.lat && c.coordinates.lng)
    .map(c => {
      const closest = findClosestRoutePoint(routeCoords, c.coordinates.lat, c.coordinates.lng);
      const kmAlong = distanceAlongRoute(routeCoords, closest.index, totalDistanceKm);
      return { ...c, routeDistance: closest.distance, kmAlongRoute: kmAlong };
    })
    .filter(c => c.routeDistance <= maxDeviation) // Within deviation km of route
    .sort((a, b) => a.kmAlongRoute - b.kmAlongRoute);

  if (chargersOnRoute.length === 0) return { noChargers: true };

  const favoriteIds = new Set((favoriteChargers || []).map(f => f.id));

  // Helper: find valid chargers at approximately km position
  function findChargersNear(kmPos, rangeKm = 30) {
    return chargersOnRoute.filter(c => 
      Math.abs(c.kmAlongRoute - kmPos) <= rangeKm
    );
  }

  // Helper: put favorite chargers first while preserving the rest of the sort
  function applyFavoritePriority(sortedList) {
    if (!priorityFavorites || favoriteIds.size === 0) return sortedList;
    const favs = [];
    const nonFavs = [];
    for (const c of sortedList) {
      if (favoriteIds.has(c.id)) favs.push(c);
      else nonFavs.push(c);
    }
    return [...favs, ...nonFavs];
  }

  // Generate stop positions needed
  function generateStopPositions() {
    const positions = [];
    let currentKm = 0;
    let currentRange = startRangeKm;
    
    while (true) {
      const nextStopKm = currentKm + currentRange;
      const remainingKm = totalDistanceKm - nextStopKm;
      
      // Can we reach destination from next stop with a charge?
      const rangeAfterCharge = ((maxBatteryCharge - arrivalBattery) / 100) * batteryCapacity * kmPerKwh;
      
      if (nextStopKm >= totalDistanceKm) break; // Can reach without more stops
      if (remainingKm <= rangeAfterCharge) {
        positions.push(nextStopKm);
        break;
      }
      positions.push(nextStopKm);
      currentKm = nextStopKm;
      currentRange = chargeRangeKm;
    }
    return positions;
  }

  const stopPositions = generateStopPositions();
  if (stopPositions.length === 0) return { noStopsNeeded: true };

  // Strategy: for each stop position, find best charger candidates
  function buildRoute(strategy) {
    const stops = [];
    let batteryPct = startBattery;
    
    for (let i = 0; i < stopPositions.length; i++) {
      const targetKm = stopPositions[i];
      const candidates = findChargersNear(targetKm, 100);
      
      if (candidates.length === 0) return null; // Can't build this route
      
      let selected;
      if (strategy === 'fastest') {
        // Prefer highest power chargers >= car's max charge power
        selected = candidates
          .filter(c => (c.maxPower || 0) >= maxChargePower)
          .sort((a, b) => (b.maxPower || 0) - (a.maxPower || 0) || a.routeDistance - b.routeDistance);
        if (selected.length === 0) {
          selected = candidates.sort((a, b) => (b.maxPower || 0) - (a.maxPower || 0));
        }
        selected = applyFavoritePriority(selected);
      } else if (strategy === 'cheapest') {
        // Only chargers with known price
        selected = candidates
          .filter(c => parsePrice(c.usageCost) !== null)
          .sort((a, b) => parsePrice(a.usageCost) - parsePrice(b.usageCost) || a.routeDistance - b.routeDistance);
        if (selected.length === 0) return null; // No priced chargers
        selected = applyFavoritePriority(selected);
      } else if (strategy === 'optimal') {
        // Balance speed and price
        selected = candidates
          .map(c => {
            const price = parsePrice(c.usageCost);
            const power = Math.min(c.maxPower || 0, maxChargePower);
            // Score: lower is better. Penalize low power and high price
            const priceScore = price !== null ? price : 0.50; // assume avg if unknown
            const speedScore = power > 0 ? (1 / power) * 100 : 10;
            return { ...c, score: priceScore * 0.5 + speedScore * 0.5 };
          })
          .sort((a, b) => a.score - b.score);
        selected = applyFavoritePriority(selected);
      } else if (strategy === 'favorites') {
        // Only favorite chargers. If no favorites at this position, this stop cannot be built.
        const favs = candidates.filter(c => favoriteIds.has(c.id));
        if (favs.length === 0) return null;
        selected = favs.sort((a, b) => (b.maxPower || 0) - (a.maxPower || 0));
      } else if (strategy === 'shortest') {
        // Least deviation first, then speed, then price
        selected = candidates
          .map(c => {
            const power = Math.min(c.maxPower || 0, maxChargePower);
            const price = parsePrice(c.usageCost);
            const deviationScore = c.routeDistance; // primary: km deviation
            const speedScore = power > 0 ? (1 / power) * 50 : 10; // secondary
            const priceScore = price !== null ? price * 0.1 : 0.05; // tertiary
            return { ...c, score: deviationScore * 10 + speedScore + priceScore };
          })
          .sort((a, b) => a.score - b.score);
        selected = applyFavoritePriority(selected);
      }
      
      if (!selected || selected.length === 0) return null;
      const charger = selected[0];
      
      // Calculate battery at arrival to this charger (including deviation)
      const legKmOnRoute = i === 0 ? charger.kmAlongRoute : (charger.kmAlongRoute - (stops[i - 1]?.charger.kmAlongRoute || 0));
      const deviationKm = charger.routeDistance * 2; // round-trip: off route + back
      const distToCharger = legKmOnRoute + deviationKm;
      const kwhUsed = distToCharger * consumption / 100;
      const pctUsed = (kwhUsed / batteryCapacity) * 100;
      const arrivalPct = Math.max(0, batteryPct - pctUsed);
      
      // Charge to maxBatteryCharge
      const departurePct = maxBatteryCharge;
      const kwhCharged = ((departurePct - arrivalPct) / 100) * batteryCapacity;
      const chargerPower = Math.min(charger.maxPower || 50, maxChargePower);
      const chargeMinutes = estimateChargeTime(kwhCharged, chargerPower, maxChargePower, batteryCapacity, arrivalPct, departurePct);
      const price = parsePrice(charger.usageCost);
      const cost = price !== null ? kwhCharged * price : null;
      
      stops.push({
        charger,
        arrivalPct: Math.round(arrivalPct),
        departurePct,
        kwhCharged: Math.round(kwhCharged * 10) / 10,
        chargeMinutes: Math.round(chargeMinutes),
        cost: cost !== null ? Math.round(cost * 100) / 100 : null,
        pricePerKwh: price,
        deviationKm: Math.round(deviationKm * 10) / 10,
        chargerPowerKw: charger.maxPower || 0,
      });
      
      batteryPct = departurePct;
    }
    
    // Calculate final arrival battery
    const lastStopKm = stops.length > 0 ? stops[stops.length - 1].charger.kmAlongRoute : 0;
    const finalLegKm = totalDistanceKm - lastStopKm;
    const finalKwhUsed = finalLegKm * consumption / 100;
    const finalPctUsed = (finalKwhUsed / batteryCapacity) * 100;
    const finalArrivalPct = Math.max(0, batteryPct - finalPctUsed);
    
    const totalChargeTime = stops.reduce((sum, s) => sum + s.chargeMinutes, 0);
    const totalCost = stops.filter(s => s.cost !== null).reduce((sum, s) => sum + s.cost, 0);
    const hasAllPrices = stops.every(s => s.cost !== null);
    const totalDeviationKm = stops.reduce((sum, s) => sum + s.deviationKm, 0);
    
    return {
      stops,
      finalArrivalPct: Math.round(finalArrivalPct),
      totalChargeTime,
      totalCost: hasAllPrices ? Math.round(totalCost * 100) / 100 : null,
      hasAllPrices,
      totalDeviationKm: Math.round(totalDeviationKm * 10) / 10,
    };
  }

  // Generate multiple route variants by using different chargers at each position
  function generateVariants(strategy, count = 3) {
    const variants = [];
    const usedChargerSets = [];
    
    for (let attempt = 0; attempt < count * 3 && variants.length < count; attempt++) {
      const route = buildRouteVariant(strategy, usedChargerSets);
      if (route) {
        const key = route.stops.map(s => s.charger.id).join('-');
        if (!usedChargerSets.includes(key)) {
          usedChargerSets.push(key);
          variants.push(route);
        }
      }
    }
    return variants;
  }

  function buildRouteVariant(strategy, excludeKeys) {
    const stops = [];
    let batteryPct = startBattery;
    
    for (let i = 0; i < stopPositions.length; i++) {
      const targetKm = stopPositions[i];
      let candidates = findChargersNear(targetKm, 100);

      if (candidates.length === 0) return null;

      // Exclude already-used combinations
      candidates = candidates.filter(c => {
        const potentialKey = [...stops.map(s => s.charger.id), c.id].join('-');
        return true; // We'll filter full routes later
      });

      let sorted;
      if (strategy === 'fastest') {
        sorted = candidates
          .filter(c => (c.maxPower || 0) >= maxChargePower * 0.8)
          .sort((a, b) => (b.maxPower || 0) - (a.maxPower || 0) || a.routeDistance - b.routeDistance);
        if (sorted.length === 0) sorted = candidates.sort((a, b) => (b.maxPower || 0) - (a.maxPower || 0));
        sorted = applyFavoritePriority(sorted);
      } else if (strategy === 'cheapest') {
        sorted = candidates
          .filter(c => parsePrice(c.usageCost) !== null)
          .sort((a, b) => parsePrice(a.usageCost) - parsePrice(b.usageCost) || a.routeDistance - b.routeDistance);
        if (sorted.length === 0) return null;
        sorted = applyFavoritePriority(sorted);
      } else if (strategy === 'optimal') {
        sorted = candidates.map(c => {
          const price = parsePrice(c.usageCost);
          const power = Math.min(c.maxPower || 0, maxChargePower);
          const priceScore = price !== null ? price : 0.50;
          const speedScore = power > 0 ? (1 / power) * 100 : 10;
          return { ...c, score: priceScore * 0.5 + speedScore * 0.5 };
        }).sort((a, b) => a.score - b.score);
        sorted = applyFavoritePriority(sorted);
      } else if (strategy === 'favorites') {
        const favs = candidates.filter(c => favoriteIds.has(c.id));
        if (favs.length === 0) return null;
        sorted = favs.sort((a, b) => (b.maxPower || 0) - (a.maxPower || 0));
      } else if (strategy === 'shortest') {
        sorted = candidates.map(c => {
          const power = Math.min(c.maxPower || 0, maxChargePower);
          const price = parsePrice(c.usageCost);
          const deviationScore = c.routeDistance;
          const speedScore = power > 0 ? (1 / power) * 50 : 10;
          const priceScore = price !== null ? price * 0.1 : 0.05;
          return { ...c, score: deviationScore * 10 + speedScore + priceScore };
        }).sort((a, b) => a.score - b.score);
        sorted = applyFavoritePriority(sorted);
      }

      if (!sorted || sorted.length === 0) return null;
      
      // Pick different charger than previously used variants
      const variantIdx = excludeKeys.length % Math.min(sorted.length, 3);
      const charger = sorted[Math.min(variantIdx, sorted.length - 1)];
      
      const legKmOnRoute = i === 0 ? charger.kmAlongRoute : (charger.kmAlongRoute - (stops[i - 1]?.charger.kmAlongRoute || 0));
      const deviationKm = charger.routeDistance * 2; // round-trip deviation
      const distToCharger = legKmOnRoute + deviationKm;
      const kwhUsed = distToCharger * consumption / 100;
      const pctUsed = (kwhUsed / batteryCapacity) * 100;
      const arrivalPct = Math.max(0, batteryPct - pctUsed);
      const departurePct = maxBatteryCharge;
      const kwhCharged = ((departurePct - arrivalPct) / 100) * batteryCapacity;
      const chargerPower = Math.min(charger.maxPower || 50, maxChargePower);
      const chargeMinutes = estimateChargeTime(kwhCharged, chargerPower, maxChargePower, batteryCapacity, arrivalPct, departurePct);
      const price = parsePrice(charger.usageCost);
      const cost = price !== null ? kwhCharged * price : null;
      
      stops.push({
        charger,
        arrivalPct: Math.round(arrivalPct),
        departurePct,
        kwhCharged: Math.round(kwhCharged * 10) / 10,
        chargeMinutes: Math.round(chargeMinutes),
        cost: cost !== null ? Math.round(cost * 100) / 100 : null,
        pricePerKwh: price,
        deviationKm: Math.round(deviationKm * 10) / 10,
        chargerPowerKw: charger.maxPower || 0,
      });
      batteryPct = departurePct;
    }
    
    const lastStopKm = stops.length > 0 ? stops[stops.length - 1].charger.kmAlongRoute : 0;
    const finalLegKm = totalDistanceKm - lastStopKm;
    const finalKwhUsed = finalLegKm * consumption / 100;
    const finalPctUsed = (finalKwhUsed / batteryCapacity) * 100;
    const finalArrivalPct = Math.max(0, batteryPct - finalPctUsed);
    const totalChargeTime = stops.reduce((sum, s) => sum + s.chargeMinutes, 0);
    const totalCost = stops.filter(s => s.cost !== null).reduce((sum, s) => sum + s.cost, 0);
    const hasAllPrices = stops.every(s => s.cost !== null);
    const totalDeviationKm = stops.reduce((sum, s) => sum + s.deviationKm, 0);
    
    return {
      stops,
      finalArrivalPct: Math.round(finalArrivalPct),
      totalChargeTime,
      totalCost: hasAllPrices ? Math.round(totalCost * 100) / 100 : null,
      hasAllPrices,
      totalDeviationKm: Math.round(totalDeviationKm * 10) / 10,
    };
  }

  // Generate all categories
  const favorites = generateVariants('favorites', 3);
  const fastest = generateVariants('fastest', 3);
  const cheapest = generateVariants('cheapest', 3);
  const optimal = generateVariants('optimal', 3);
  const shortest = generateVariants('shortest', 3);

  return { favorites, fastest, cheapest, optimal, shortest, stopPositions };
}

export default function RoutesPopup({
  visible, onClose, route, totalDistanceKm, evCarConfig,
  favoriteChargers, filters, maxDeviation, onSelectRoute,
  excludedOperators, excludedConnectors, excludedPowerRanges,
  mapChargers,
}) {
  const [routeOptions, setRouteOptions] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedIdx, setSelectedIdx] = useState(null);
  const [chargers, setChargers] = useState([]);
  const [priorityFavorites, setPriorityFavorites] = useState(false);
  const dragRef = useRef(null);
  const [pos, setPos] = useState({ x: 60, y: 80 });
  const [dragging, setDragging] = useState(false);
  const dragOffset = useRef({ x: 0, y: 0 });

  // Dragging logic
  const onMouseDown = useCallback((e) => {
    if (e.target.closest('.routes-popup-body')) return;
    setDragging(true);
    dragOffset.current = { x: e.clientX - pos.x, y: e.clientY - pos.y };
  }, [pos]);

  useEffect(() => {
    if (!dragging) return;
    const onMove = (e) => setPos({ x: e.clientX - dragOffset.current.x, y: e.clientY - dragOffset.current.y });
    const onUp = () => setDragging(false);
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => { window.removeEventListener('mousemove', onMove); window.removeEventListener('mouseup', onUp); };
  }, [dragging]);

  // Use ONLY the chargers that are currently drawn on the map (from stop
  // points and exploration pins). The popup does NOT fetch chargers from the
  // API by sampling the route geometry anymore — the user must add stop
  // points or exploration points on the map for routes to be calculated.
  useEffect(() => {
    if (!visible) { setChargers([]); setLoading(false); return; }
    if (!route || !route.geometry?.coordinates) { setChargers([]); setLoading(false); return; }
    setChargers(Array.isArray(mapChargers) ? mapChargers : []);
    setLoading(false);
  }, [visible, route, mapChargers]);

  // Compute route options when chargers are loaded
  useEffect(() => {
    if (!chargers.length || !route?.geometry?.coordinates || !evCarConfig) {
      setRouteOptions(null);
      return;
    }
    // Filter out excluded operators, connectors, and power ranges
    let filteredChargers = chargers;
    if (excludedOperators && excludedOperators.size > 0) {
      filteredChargers = filteredChargers.filter(c => !c.operator || !excludedOperators.has(c.operator));
    }
    if (excludedConnectors && excludedConnectors.size > 0) {
      filteredChargers = filteredChargers.filter(c => {
        // Keep charger if at least one of its connectors is NOT excluded
        if (!c.connectors || c.connectors.length === 0) return true;
        return c.connectors.some(conn => !excludedConnectors.has(conn.type));
      });
    }
    if (excludedPowerRanges && excludedPowerRanges.size > 0) {
      // Power range indices: 0=0-22kW, 1=23-50kW, 2=51-150kW, 3=151+kW
      const ranges = [[0,22],[23,50],[51,150],[151,99999]];
      filteredChargers = filteredChargers.filter(c => {
        const power = c.maxPower || 0;
        for (const idx of excludedPowerRanges) {
          const [min, max] = ranges[idx] || [0, 0];
          if (power >= min && power <= max) return false;
        }
        return true;
      });
    }
    // All route types (fastest/cheapest/optimal/shortest) work with the full
    // list of chargers available along the route; the "favoritas" category
    // is the only one that restricts to favorites. The `priorityFavorites`
    // toggle, when enabled, will simply push favorite chargers to the top
    // of each strategy's sorted list.
    const options = computeRouteOptions(
      route.geometry.coordinates,
      totalDistanceKm,
      evCarConfig,
      filteredChargers,
      favoriteChargers,
      maxDeviation || 10,
      priorityFavorites
    );
    setRouteOptions(options);
  }, [chargers, route, totalDistanceKm, evCarConfig, favoriteChargers, maxDeviation, excludedOperators, excludedConnectors, excludedPowerRanges, priorityFavorites]);

  const handleSelectRoute = useCallback((category, idx) => {
    // Toggle: clicking the already-selected route deselects it.
    if (selectedCategory === category && selectedIdx === idx) {
      setSelectedCategory(null);
      setSelectedIdx(null);
      if (onSelectRoute) onSelectRoute(null);
      return;
    }
    setSelectedCategory(category);
    setSelectedIdx(idx);
    if (routeOptions && routeOptions[category] && routeOptions[category][idx]) {
      onSelectRoute && onSelectRoute(routeOptions[category][idx]);
    }
  }, [routeOptions, onSelectRoute, selectedCategory, selectedIdx]);

  if (!visible) return null;

  const renderStop = (stop, idx) => (
    <div key={idx} style={{ display: 'flex', gap: 5, fontSize: '0.73rem', padding: '3px 0', borderBottom: '1px solid #f1f5f9', alignItems: 'center', flexWrap: 'wrap' }}>
      <span style={{ fontWeight: 600, color: '#2563eb', minWidth: 18 }}>#{idx + 1}</span>
      <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 60 }} title={stop.charger.name}>
        {stop.charger.operator || stop.charger.name}
      </span>
      <span title="Potencia cargador" style={{ color: '#7c3aed' }}>⚡{stop.chargerPowerKw}kW</span>
      <span title="Llegada">🔋{stop.arrivalPct}%</span>
      <span title="Salida">→{stop.departurePct}%</span>
      <span title="kWh cargados">{stop.kwhCharged}kWh</span>
      <span title="Tiempo carga">⏱{stop.chargeMinutes}min</span>
      {stop.deviationKm > 0 && <span title="Desvío ida y vuelta" style={{ color: '#dc2626' }}>↗{stop.deviationKm}km</span>}
      {stop.cost !== null ? <span title="Coste">💰{stop.cost.toFixed(2)}€</span> : <span style={{color:'#9ca3af'}}>—</span>}
    </div>
  );

  const renderRouteCard = (routeData, idx, category) => {
    if (!routeData) return null;
    const isSelected = selectedCategory === category && selectedIdx === idx;
    return (
      <div
        key={idx}
        onClick={() => handleSelectRoute(category, idx)}
        style={{
          border: isSelected ? '2px solid #2563eb' : '1px solid #e2e8f0',
          borderRadius: 8,
          padding: '8px 10px',
          marginBottom: 6,
          cursor: 'pointer',
          background: isSelected ? '#eff6ff' : '#fff',
          transition: 'all 0.15s',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
          <span style={{ fontWeight: 600, fontSize: '0.8rem', color: '#1e293b' }}>
            Ruta {idx + 1} • {routeData.stops.length} parada{routeData.stops.length !== 1 ? 's' : ''}
          </span>
          <span style={{ fontSize: '0.75rem', color: '#64748b' }}>
            ⏱{routeData.totalChargeTime}min
            {routeData.totalCost !== null && ` • 💰${routeData.totalCost.toFixed(2)}€`}
          </span>
        </div>
        <div style={{ fontSize: '0.72rem', color: '#64748b', marginBottom: 4 }}>
          Llegas con {routeData.finalArrivalPct}% batería
          {routeData.totalDeviationKm > 0 && <span style={{ color: '#dc2626', marginLeft: 8 }}>↗ Desvío total: {routeData.totalDeviationKm}km</span>}
        </div>
        {routeData.stops.map((stop, i) => renderStop(stop, i))}
      </div>
    );
  };

  const renderCategory = (title, icon, routes, categoryKey) => (
    <div style={{ marginBottom: 12 }}>
      <h4 style={{ margin: '0 0 6px 0', fontSize: '0.9rem', color: '#1e293b', display: 'flex', alignItems: 'center', gap: 6 }}>
        {icon} {title}
      </h4>
      {routes && routes.length > 0 ? (
        routes.map((r, i) => renderRouteCard(r, i, categoryKey))
      ) : (
        <div style={{ fontSize: '0.78rem', color: '#9ca3af', fontStyle: 'italic', padding: '4px 0' }}>
          No hay rutas disponibles para esta categoría
        </div>
      )}
    </div>
  );

  return (
    <div
      ref={dragRef}
      onMouseDown={onMouseDown}
      style={{
        position: 'fixed',
        left: pos.x,
        top: pos.y,
        zIndex: 10000,
        width: 420,
        maxHeight: '80vh',
        background: '#fff',
        borderRadius: 12,
        boxShadow: '0 8px 32px rgba(0,0,0,0.22)',
        display: 'flex',
        flexDirection: 'column',
        cursor: dragging ? 'grabbing' : 'default',
        userSelect: dragging ? 'none' : 'auto',
      }}
    >
      {/* Header - draggable area */}
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        padding: '10px 14px', borderBottom: '1px solid #e2e8f0', cursor: 'grab',
        borderRadius: '12px 12px 0 0', background: '#f8fafc',
      }}>
        <h3 style={{ margin: 0, fontSize: '1rem', color: '#1e293b', display: 'flex', alignItems: 'center', gap: 6 }}>
          🛣️ Rutas calculadas
        </h3>
        <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: '1.2rem', cursor: 'pointer', color: '#6b7280', padding: '2px 6px' }}>✕</button>
      </div>

      {/* Body */}
      <div className="routes-popup-body" style={{ overflowY: 'auto', padding: '12px 14px', flex: 1 }}>
        {loading && (
          <div style={{ textAlign: 'center', padding: 20, color: '#64748b' }}>
            <div style={{ fontSize: '1.5rem', marginBottom: 8 }}>⏳</div>
            Buscando cargadores a lo largo de la ruta...
          </div>
        )}

        {!loading && routeOptions?.noStopsNeeded && (
          <div style={{ textAlign: 'center', padding: 20, color: '#059669' }}>
            <div style={{ fontSize: '1.5rem', marginBottom: 8 }}>✅</div>
            <strong>No necesitas parar a cargar</strong>
            <div style={{ fontSize: '0.85rem', marginTop: 4, color: '#64748b' }}>
              Tu batería inicial ({evCarConfig.startBattery}%) es suficiente para cubrir los {totalDistanceKm?.toFixed(0)} km
            </div>
          </div>
        )}

        {!loading && (!mapChargers || mapChargers.length === 0) && !routeOptions?.noStopsNeeded && (
          <div style={{ textAlign: 'center', padding: 20, color: '#b45309' }}>
            <div style={{ fontSize: '1.5rem', marginBottom: 8 }}>📍</div>
            <strong>Añade puntos de parada o exploración en el mapa</strong>
            <div style={{ fontSize: '0.85rem', marginTop: 4, color: '#64748b' }}>
              Las rutas se calculan únicamente con los cargadores que tengas dibujados en el mapa.
              <br />Para añadirlos:
              <br />• Ve a la pestaña <strong>Paradas</strong> y añade puntos km, o
              <br />• Haz <strong>doble clic</strong> sobre la ruta o fuera de ella para crear puntos de exploración.
            </div>
          </div>
        )}

        {!loading && routeOptions?.noChargers && mapChargers && mapChargers.length > 0 && (
          <div style={{ textAlign: 'center', padding: 20, color: '#dc2626' }}>
            <div style={{ fontSize: '1.5rem', marginBottom: 8 }}>⚠️</div>
            <strong>No se encontraron cargadores cerca de la ruta</strong>
            <div style={{ fontSize: '0.85rem', marginTop: 4, color: '#64748b' }}>
              Intenta aumentar el radio de desviación máxima
            </div>
          </div>
        )}

        {/* Top controls: priority favorites toggle */}
        {!loading && routeOptions && !routeOptions.noStopsNeeded && !routeOptions.noChargers && (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            padding: '8px 10px',
            marginBottom: 10,
            background: priorityFavorites ? '#fef3c7' : '#f8fafc',
            border: priorityFavorites ? '1px solid #fbbf24' : '1px solid #e2e8f0',
            borderRadius: 8,
            transition: 'all 0.15s',
          }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: '0.82rem', color: '#1e293b', userSelect: 'none' }}>
              <input
                type="checkbox"
                checked={priorityFavorites}
                onChange={(e) => setPriorityFavorites(e.target.checked)}
                style={{ cursor: 'pointer', accentColor: '#f59e0b' }}
              />
              <span style={{ fontWeight: 600 }}>⭐ Prioridad favoritos</span>
            </label>
            <span style={{ fontSize: '0.72rem', color: '#64748b' }}>
              {priorityFavorites
                ? 'Priorizando cargadores favoritos en todas las rutas'
                : 'Activar para priorizar favoritos en cualquier tipo de ruta'}
              </span>
          </div>
        )}

        {!loading && routeOptions && !routeOptions.noStopsNeeded && !routeOptions.noChargers && (
          <>
            {renderCategory('Más rápidas', '⚡', routeOptions.fastest, 'fastest')}
            {renderCategory('Más baratas', '💰', routeOptions.cheapest, 'cheapest')}
            {renderCategory('Más óptimas', '⚖️', routeOptions.optimal, 'optimal')}
            {renderCategory('Más cortas', '📍', routeOptions.shortest, 'shortest')}
            {renderCategory('Rutas favoritas', '⭐', routeOptions.favorites, 'favorites')}
            {(!routeOptions.favorites || routeOptions.favorites.length === 0) && (favoriteChargers || []).length === 0 && (
              <div style={{ fontSize: '0.75rem', color: '#9ca3af', fontStyle: 'italic', padding: '2px 0 10px 4px' }}>
                Marca cargadores como favoritos (estrella en el mapa o en la pestaña Favoritos) para ver rutas con paradas en ellos.
              </div>
            )}
            {(!routeOptions.favorites || routeOptions.favorites.length === 0) && (favoriteChargers || []).length > 0 && (
              <div style={{ fontSize: '0.75rem', color: '#9ca3af', fontStyle: 'italic', padding: '2px 0 10px 4px' }}>
                Tus cargadores favoritos no están dentro del radio de la ruta calculada.
              </div>
            )}
          </>
        )}

        {!loading && !routeOptions && !route && (
          <div style={{ textAlign: 'center', padding: 20, color: '#64748b' }}>
            <div style={{ fontSize: '1.5rem', marginBottom: 8 }}>🗺️</div>
            <strong>Calcula una ruta primero</strong>
            <div style={{ fontSize: '0.85rem', marginTop: 4 }}>
              Indica un origen y destino para ver las rutas calculadas con paradas de carga
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
